import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SceneWithUserNames } from '../../../../types/scene.types';
import { ElementType } from '../../../../types/element.types';
import { SceneRef } from '../../SceneDetails/SceneDetails';
import { MenuPosition } from '../BreakdownMain.types';
import { useElementUpdates } from '../../../../hooks/dataUpdates/useElementUpdates';

/**
 * 搜索相关处理 Hook
 * 处理场景搜索、地点搜索和场次元素搜索的相关操作
 */
export const useSearchHandlers = (
  scenes: SceneWithUserNames[],
  breakdownId: string | undefined,
  // SetSearch 相关状态
  activeSceneId: string | null,
  setActiveSceneId: React.Dispatch<React.SetStateAction<string | null>>,
  setSearchVisible: boolean,
  setSetSearchVisible: React.Dispatch<React.SetStateAction<boolean>>,
  setSearchText: string,
  setSetSearchText: React.Dispatch<React.SetStateAction<string>>,
  setSetSearchPosition: React.Dispatch<React.SetStateAction<MenuPosition | null>>,
  setSetPreviewText: React.Dispatch<React.SetStateAction<string | null>>,
  setLocalSceneSetDisplay: React.Dispatch<React.SetStateAction<Map<string, string>>>,
  // 地点搜索相关状态
  activeLocationSceneId: string | null,
  setActiveLocationSceneId: React.Dispatch<React.SetStateAction<string | null>>,
  sceneLocationSearchVisible: boolean,
  setSceneLocationSearchVisible: React.Dispatch<React.SetStateAction<boolean>>,
  sceneLocationSearchText: string,
  setSceneLocationSearchText: React.Dispatch<React.SetStateAction<string>>,
  setSceneLocationSearchPosition: React.Dispatch<React.SetStateAction<MenuPosition | null>>,
  setSceneLocationPreviewText: React.Dispatch<React.SetStateAction<string | null>>,
  setLocalSceneLocationDisplay: React.Dispatch<React.SetStateAction<Map<string, string>>>,
  setSceneLocationDisplayTexts: React.Dispatch<React.SetStateAction<{ [sceneId: string]: string }>>,
  // ElementSearch 相关状态
  setElementSearchVisible: React.Dispatch<React.SetStateAction<boolean>>,
  setElementSearchType: React.Dispatch<React.SetStateAction<ElementType>>,
  // ElementSearch：当前场次下已选元素 ID（用于勾选态），由缓存/本地合并更新，不依赖全量 GET
  selectedSceneId: string | null,
  setSelectedElementIds: React.Dispatch<React.SetStateAction<Set<string>>>,
  // SceneRef
  sceneRef: React.RefObject<SceneRef>,
  // CRUD 操作函数
  handleSceneUpdate: (sceneId: string, field: keyof SceneWithUserNames, value: any) => void,
  // 单元格导航函数
  finishCellEdit: (rowIndex: number, columnKey: string, skipUpdate?: boolean) => void
) => {
  const { t } = useTranslation();
  const { createElementRelationData } = useElementUpdates();
  
  // 处理场景推荐的关闭
  const handleSetSearchClose = useCallback(() => {
    // 先保存当前 activeSceneId，因为后面会设置为 null
    const currentActiveSceneId = activeSceneId;
    
    setSetSearchVisible(false);
    setActiveSceneId(null);
    setSetSearchPosition(null); // 重置位置信息
    setSetPreviewText(null); // 清除预览文本
    
    // 如果有active场次，保持搜索框显示当前选中的场景集合名称
    if (currentActiveSceneId) {
      const currentScene = scenes.find(s => s.sceneID === currentActiveSceneId);
      setSetSearchText(currentScene?.sceneSet || '');
    } else {
      setSetSearchText('');
    }
  }, [scenes, activeSceneId, setSetSearchVisible, setActiveSceneId, setSetSearchPosition, setSetPreviewText, setSetSearchText]);

  // 处理地点推荐的关闭
  const handleLocationSearchClose = useCallback(() => {
    // 先保存当前 activeLocationSceneId，因为后面会设置为 null
    const currentActiveLocationSceneId = activeLocationSceneId;
    
    setSceneLocationSearchVisible(false);
    setActiveLocationSceneId(null);
    setSceneLocationSearchPosition(null); // 重置位置信息
    setSceneLocationPreviewText(null); // 清除预览文本
    
    // 如果有active场次，保持搜索框显示当前选中的地点
    if (currentActiveLocationSceneId) {
      const currentScene = scenes.find(s => s.sceneID === currentActiveLocationSceneId);
      setSceneLocationSearchText(currentScene?.sceneLocation || '');
    } else {
      setSceneLocationSearchText('');
    }
  }, [scenes, activeLocationSceneId, setSceneLocationSearchVisible, setActiveLocationSceneId, setSceneLocationSearchPosition, setSceneLocationPreviewText, setSceneLocationSearchText]);

  // 处理场景推荐选择（写入 sceneSet 文本）
  const handleSetRecommendChange = useCallback((text: string | null) => {
    if (activeSceneId && text !== null) {
      // 先更新所有状态
      handleSceneUpdate(activeSceneId, 'sceneSet', text);
      setSetSearchText(text);
      // 更新本地显示值，确保输入框显示正确的值
      setLocalSceneSetDisplay(prev => {
        const nextMap = new Map(prev);
        nextMap.set(activeSceneId, text);
        return nextMap;
      });
      // 延迟关闭搜索，确保状态更新完成后再关闭
      setTimeout(() => {
        handleSetSearchClose();
      }, 0);
    }
  }, [activeSceneId, handleSceneUpdate, setSetSearchText, setLocalSceneSetDisplay, handleSetSearchClose]);

  // 处理地点推荐选择
  const handleLocationRecommendChange = useCallback((text: string | null) => {
    if (activeLocationSceneId && text !== null) {
      // 先更新所有状态
      handleSceneUpdate(activeLocationSceneId, 'sceneLocation', text);
      setSceneLocationSearchText(text);
      setSceneLocationDisplayTexts(prev => ({
        ...prev,
        [activeLocationSceneId]: text
      }));
      // 更新本地显示值，确保输入框显示正确的值
      setLocalSceneLocationDisplay(prev => {
        const nextMap = new Map(prev);
        nextMap.set(activeLocationSceneId, text);
        return nextMap;
      });
      
      // 延迟关闭搜索，确保状态更新完成后再关闭
      setTimeout(() => {
        handleLocationSearchClose();
      }, 0);
    }
  }, [activeLocationSceneId, handleSceneUpdate, setSceneLocationSearchText, setSceneLocationDisplayTexts, setLocalSceneLocationDisplay, handleLocationSearchClose]);

  // 处理点击场景输入框
  const handleSetInputClick = useCallback((sceneId: string, currentSetName?: string, event?: React.MouseEvent) => {
    console.log('handleSetInputClick called - sceneId:', sceneId, 'currentSetName:', currentSetName);
    setActiveSceneId(sceneId);
    setSetSearchVisible(true);
    // 保留已有内容作为搜索文本和本地显示值
    const currentValue = currentSetName || '';
    setSetSearchText(currentValue);
    setLocalSceneSetDisplay(prev => {
      const nextMap = new Map(prev);
      nextMap.set(sceneId, currentValue);
      return nextMap;
    });
    console.log('SetSearch should now be visible');
    
    // 计算点击的输入框位置（相对于表格容器）
    if (event && event.currentTarget) {
      const inputElement = event.currentTarget as HTMLElement;
      const tableContainer = document.querySelector('.breakdown-table-container') as HTMLElement;
      
      if (inputElement && tableContainer) {
        const inputRect = inputElement.getBoundingClientRect();
        const containerRect = tableContainer.getBoundingClientRect();
        
        // 计算相对于表格容器的位置，宽度匹配输入框宽度
        setSetSearchPosition({
          top: inputRect.bottom - containerRect.top + 4,
          left: inputRect.left - containerRect.left,
          width: inputRect.width
        });
      }
    }
  }, [setActiveSceneId, setSetSearchVisible, setSetSearchText, setLocalSceneSetDisplay, setSetSearchPosition]);

  // 处理点击地点输入框
  const handleLocationInputClick = useCallback((sceneId: string, currentLocation?: string, event?: React.MouseEvent) => {
    setActiveLocationSceneId(sceneId);
    setSceneLocationSearchVisible(true);
    // 保留已有内容作为搜索文本和本地显示值
    const currentValue = currentLocation || '';
    setSceneLocationSearchText(currentValue);
    setLocalSceneLocationDisplay(prev => {
      const nextMap = new Map(prev);
      nextMap.set(sceneId, currentValue);
      return nextMap;
    });
    
    // 计算点击的输入框位置（相对于表格容器）
    if (event && event.currentTarget) {
      const inputElement = event.currentTarget as HTMLElement;
      const tableContainer = document.querySelector('.breakdown-table-container') as HTMLElement;
      
      if (inputElement && tableContainer) {
        const inputRect = inputElement.getBoundingClientRect();
        const containerRect = tableContainer.getBoundingClientRect();
        
        // 计算相对于表格容器的位置，宽度匹配输入框宽度，右对齐
        const componentWidth = inputRect.width;
        setSceneLocationSearchPosition({
          top: inputRect.bottom - containerRect.top + 4,
          left: inputRect.right - containerRect.left - componentWidth,
          width: componentWidth
        });
      }
    }
  }, [setActiveLocationSceneId, setSceneLocationSearchVisible, setSceneLocationSearchText, setLocalSceneLocationDisplay, setSceneLocationSearchPosition]);

  // 处理ElementSearch的onChange回调
  const handleElementSearchChange = useCallback(async (element: any, isCreated?: boolean) => {
    console.log('Breakdown收到ElementSearch的onChange回调:', element, 'isCreated:', isCreated);
    
    if (!selectedSceneId || !breakdownId) {
      console.error('缺少必要的ID信息');
      return;
    }

    // 如果是新创建的场次元素，后端在创建时已经自动创建了关系，不需要再次创建
    if (isCreated) {
      console.log('场次元素是新创建的，后端已自动创建关系，跳过重复创建');
      setSelectedElementIds((prev) => new Set(prev).add(element.elementID));
      return;
    }

    try {
      const selectedScene = scenes.find(s => s.sceneID === selectedSceneId);
      
      if (!selectedScene) {
        console.error('找不到选中的场次');
        return;
      }

      // 创建场次元素关系（仅当选择已存在的场次元素时）
      // 使用场次元素的最大数量作为默认数量，如果没有则使用1
      if (breakdownId) {
        await createElementRelationData(
          breakdownId,
          element.elementID,
          selectedSceneId,
          {
            elementQtyInScene: element.elementMaxQtyInBreakdown || 1,
          }
        );
        console.log('成功创建场次元素关系');
        setSelectedElementIds((prev) => new Set(prev).add(element.elementID));
      }
    } catch (error: any) {
      console.error('创建场次元素关系失败:', error);
      alert(t('scene.createElementRelationFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
    // 不再自动关闭搜索框，允许用户继续选择多个元素
    // 用户可以通过点击外部区域或关闭按钮来手动关闭
  }, [selectedSceneId, breakdownId, scenes, setSelectedElementIds, createElementRelationData, t]);

  // 处理清除Scene组件的active tab状态
  const handleClearSceneActiveTab = useCallback(() => {
    // 通过ref调用Scene组件的清除active状态方法
    if (sceneRef.current) {
      sceneRef.current.clearActiveTab();
    }
  }, [sceneRef]);

  // 处理ElementSearch的关闭
  const handleElementSearchClose = useCallback(() => {
    setElementSearchVisible(false);
    // 清除Scene组件的active tab状态
    handleClearSceneActiveTab();
  }, [setElementSearchVisible, handleClearSceneActiveTab]);

  // 打开ElementSearch
  const openElementSearch = useCallback((type: ElementType) => {
    setElementSearchType(type);
    setElementSearchVisible(true);
  }, [setElementSearchType, setElementSearchVisible]);

  return {
    // SetSearch 相关
    handleSetSearchClose,
    handleSetRecommendChange,
    handleSetInputClick,
    // LocationSearch 相关
    handleLocationSearchClose,
    handleLocationRecommendChange,
    handleLocationInputClick,
    // ElementSearch 相关
    handleElementSearchChange,
    handleElementSearchClose,
    openElementSearch,
    handleClearSceneActiveTab,
  };
};

